/**
 * Match mapping utilities for converting API data to UI components.
 */

import type { MatchBettingOdds } from "fansunited-sports-ui";
import type { FUSportsTeamCompetitor } from "fansunited-data-layer";
import type { MatchListItem } from "fansunited-sports-ui";
import { COMPETITORS } from "@/config/constants";
import { mergeTeamData } from "./team";
import { formatDateDDMM, formatDateTime, formatTimeHHMM } from "@/utilities/formatting/date";
import { FUSportsMatchWithNativeId } from "../data/types";
import { idToUrlSegment } from "@/utilities/mappers/idUrlFormat";

/** Optional function that resolves a team logo URL based on tenant display mode */
export type LogoUrlResolver = (opts: { logo?: string | null; countryFlag?: string | null; countryCode?: string | null }) => string;

// Re-export from shared styles for backward compatibility
export { getResultClass } from "@/utilities/styles/badgeStyles";

/** Live state overriding the stored match: minute and scores come from a live snapshot. */
export interface LiveOverride {
    minute: string | null;
    scoreOne: string | null;
    scoreTwo: string | null;
}

/**
 * Maps canonical FUSportsMatch to MatchListItem for the UI component
 * Merges API data with constants to enrich team information
 *
 * @param match - The match data with native IDs
 * @param isFinished - Whether the match is finished
 * @param includeYearInDate - Whether to include year in date formatting
 * @param matchUrlBuilder - URL builder function for match detail pages. Falls back to "/match/:id".
 * @param logoUrlResolver - Optional resolver that maps team metadata to the correct logo URL based on tenant display mode (logos, country-flags, shield-fallback).
 * @param live - When present the match renders as live: status "live", the minute as time, scores from the snapshot.
 */
export function mapMatchToMatchListItem(
    match: FUSportsMatchWithNativeId,
    isFinished: boolean,
    includeYearInDate: boolean = false,
    matchUrlBuilder?: (matchId: string) => string,
    logoUrlResolver?: LogoUrlResolver,
    live?: LiveOverride
): MatchListItem {
    const kickoffDate = new Date(match.timing.kickoffTime);

    // Merge match competitors with constants to get enriched data (branding, nicknames, etc.)
    const competitorOneData = match.competitorOne;
    const competitorTwoData = match.competitorTwo;

    // Type guard and merge for competitor one
    const isCompetitorOneTeam = competitorOneData.type === "club" || competitorOneData.type === "national";
    const competitorOne = isCompetitorOneTeam
        ? mergeTeamData(competitorOneData as FUSportsTeamCompetitor, COMPETITORS[competitorOneData.id])
        : competitorOneData;

    // Type guard and merge for competitor two
    const isCompetitorTwoTeam = competitorTwoData.type === "club" || competitorTwoData.type === "national";
    const competitorTwo = isCompetitorTwoTeam
        ? mergeTeamData(competitorTwoData as FUSportsTeamCompetitor, COMPETITORS[competitorTwoData.id])
        : competitorTwoData;

    // Use native match ID if available, otherwise fall back to Sportal365 ID
    const matchIdForUrl = match.fansUnitedMatchId || match.id;

    // Resolve logo URLs via tenant display mode when a resolver is provided
    const c1Team = competitorOne as FUSportsTeamCompetitor;
    const c2Team = competitorTwo as FUSportsTeamCompetitor;

    const c1LogoUrl = logoUrlResolver
        ? logoUrlResolver({ logo: competitorOne.assets?.logo, countryFlag: c1Team.country?.flag, countryCode: c1Team.country?.code })
        : competitorOne.assets?.logo || "";
    const c2LogoUrl = logoUrlResolver
        ? logoUrlResolver({ logo: competitorTwo.assets?.logo, countryFlag: c2Team.country?.flag, countryCode: c2Team.country?.code })
        : competitorTwo.assets?.logo || "";

    return {
        match: {
            id: matchIdForUrl,
            link: matchUrlBuilder ? matchUrlBuilder(matchIdForUrl) : `/match/${idToUrlSegment(matchIdForUrl)}`,
            competitorOne: {
                id: competitorOne.id,
                name: competitorOne.name,
                shortName: competitorOne.shortName,
                logo: { url: c1LogoUrl, alt: competitorOne.name },
                ...(live
                    ? { score: { main: live.scoreOne || "0" } }
                    : isFinished &&
                      match.score && {
                          score: { main: match.score.competitorOne || "0" },
                      }),
            },
            competitorTwo: {
                id: competitorTwo.id,
                name: competitorTwo.name,
                shortName: competitorTwo.shortName,
                logo: { url: c2LogoUrl, alt: competitorTwo.name },
                ...(live
                    ? { score: { main: live.scoreTwo || "0" } }
                    : isFinished &&
                      match.score && {
                          score: { main: match.score.competitorTwo || "0" },
                      }),
            },
            period: {
                label: includeYearInDate ? formatDateTime(kickoffDate) : formatDateDDMM(kickoffDate),
            },
            status: live ? "live" : isFinished ? "final" : "scheduled",
            metadata: {
                competition: match.competition?.name || "",
                date: formatDateDDMM(kickoffDate),
                // For finished matches: use 'time' field to show date (overrides "FT")
                // For future matches: use 'time' field to show time between teams
                time: live
                    ? (live.minute ?? "")
                    : isFinished
                      ? formatDateDDMM(kickoffDate)
                      : formatTimeHHMM(kickoffDate),
                // ISO string for odds eligibility check
                kickoffTime: kickoffDate.toISOString(),
                ...(!isFinished &&
                    match.venue && {
                        venue: match.venue.name,
                    }),
            },
        },
        // Add odds for upcoming matches
        ...(!isFinished && {
            bettingOdds: undefined, // Will be set by the component
        }),
    };
}
